import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/upload-ingest';
const AUDIO_VERSION = 'v2';

/**
 * Upload / Ingest page demo script — comprehensive version.
 * Covers: Direct Upload (all 4 cards in depth), Cloud Storage (S3/GCP),
 * and SQL Ingestion (3 databases, scheduling, incremental).
 */
export const uploadIngestScript: DemoScript = {
  id: 'upload-ingest',
  templateId: 'upload-ingest',
  name: 'Upload & Ingest Demo',
  description: 'Learn how to bring data into BigSplice — CSV, Excel, PDF, audio, video, images, cloud storage, and SQL databases.',
  steps: [
    // ═══════════════════════════════════════════
    // INTRO
    // ═══════════════════════════════════════════
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Let me show you everything BigSplice can ingest. We\u2019ll cover the upload cards, cloud storage connections, and SQL database imports \u2014 all from one page.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 10500 },
    },

    // ═══════════════════════════════════════════
    // TABS OVERVIEW
    // ═══════════════════════════════════════════
    {
      id: 'tabs-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="upload-tabs"]' },
      delayBefore: 200,
      subtitle: 'Three ingestion modes here. Direct upload for files on your machine, cloud storage for pulling from AWS S3 or Google Cloud, and SQL ingestion for connecting directly to PostgreSQL, MySQL, or SQL Server databases.',
      audio: { src: `${AUDIO_BASE}/tabs-highlight.mp3?${AUDIO_VERSION}`, duration: 18000 },
      cursorPath: [
        { x: -120, y: 0, duration: 400 },
        { x: 0, y: 0, duration: 500 },
        { x: 120, y: 0, duration: 500 },
      ],
    },

    // ═══════════════════════════════════════════
    // DIRECT UPLOAD — DATA FILES (deep)
    // ═══════════════════════════════════════════
    {
      id: 'data-card-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="card-data"]' },
      delayBefore: 200,
      subtitle: 'This is your data files drop zone. CSV, Excel, Parquet, JSON, TSV, and log files \u2014 all supported. Excel files are automatically converted to Parquet for fast columnar queries. Zipped archives are detected and extracted automatically, and you can upload files up to five gigabytes each.',
      audio: { src: `${AUDIO_BASE}/data-card-highlight.mp3?${AUDIO_VERSION}`, duration: 20000 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // ═══════════════════════════════════════════
    // DIRECT UPLOAD — PDF (deep: OCR + table extraction)
    // ═══════════════════════════════════════════
    {
      id: 'pdf-card-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="card-pdf"]' },
      subtitle: 'PDF documents are processed through our DeepSeek OCR pipeline. See that \u201CExtract tables\u201D checkbox? When enabled, BigSplice identifies every table in your PDF and extracts each one as a separate structured file \u2014 headers, rows, and data types fully preserved. The body text is extracted separately as clean markdown. So a financial report gives you the narrative text plus each table as a queryable spreadsheet.',
      audio: { src: `${AUDIO_BASE}/pdf-card-highlight.mp3?${AUDIO_VERSION}`, duration: 32000 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
        { x: 20, y: 40, duration: 400 },
      ],
    },

    // ═══════════════════════════════════════════
    // DIRECT UPLOAD — IMAGES (deep: all 5 modes)
    // ═══════════════════════════════════════════
    {
      id: 'image-card-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="card-image"]' },
      subtitle: 'Images get five extraction modes. Document OCR converts printed or handwritten text into structured markdown. Table extraction pulls tabular data from invoices and receipts \u2014 preserving columns and rows. Figure parsing reads data from charts and diagrams. Image description generates a natural language summary. And \u201CAll Methods\u201D runs every mode and saves the results into one folder.',
      audio: { src: `${AUDIO_BASE}/image-card-highlight.mp3?${AUDIO_VERSION}`, duration: 27000 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // ═══════════════════════════════════════════
    // DIRECT UPLOAD — AUDIO & VIDEO (deep)
    // ═══════════════════════════════════════════
    {
      id: 'media-card-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="card-media"]' },
      subtitle: 'Audio and video files are transcribed using Whisper with automatic speaker diarization \u2014 each speaker gets labeled individually. We process at up to a hundred and sixty times real-time speed on GPU. Drop in MP3, M4A, WAV, MP4, MKV, or AVI files \u2014 BigSplice handles them all.',
      audio: { src: `${AUDIO_BASE}/media-card-highlight.mp3?${AUDIO_VERSION}`, duration: 18000 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // ═══════════════════════════════════════════
    // UPLOAD SIMULATION
    // ═══════════════════════════════════════════
    {
      id: 'upload-decision',
      type: 'decision',
      target: { selector: '[data-demo="card-data"]' },
      subtitle: 'Want me to simulate a CSV upload so you can see the full pipeline, or would you prefer to upload your own file?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 5000 },
      decision: {
        prompt: 'Want me to simulate a CSV upload, or upload your own?',
        options: [
          { label: 'Use sample CSV', icon: 'database', nextStepId: 'drag-inject' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { selector: '[data-demo="card-data"]' },
      subtitle: 'Go ahead and drag a file onto any of the four cards. I\u2019ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 4500 },
      autoAdvanceAfter: 4000,
    },

    // Simulate drag-over animation
    {
      id: 'drag-inject',
      type: 'inject',
      target: { selector: '[data-demo="card-data"]' },
      subtitle: 'Here comes the file \u2014 watch the card light up.',
      audio: { src: `${AUDIO_BASE}/drag-inject.mp3?${AUDIO_VERSION}`, duration: 3000 },
      inject: {
        nodeId: 'upload-page',
        data: { demoPhase: 'drag-over', dragOverCard: 'data' },
      },
      autoAdvanceAfter: 1500,
    },

    // Upload progress: 35%
    {
      id: 'upload-start',
      type: 'inject',
      target: { selector: '[data-demo="upload-queue"]' },
      subtitle: 'Your file is uploading now. The queue on the right tracks progress for every file in the pipeline.',
      audio: { src: `${AUDIO_BASE}/upload-progress.mp3?${AUDIO_VERSION}`, duration: 7000 },
      inject: {
        nodeId: 'upload-page',
        data: {
          demoPhase: 'upload-start',
          dragOverCard: null,
          uploadingFiles: [
            { fileName: 'quarterly_revenue.csv', fileSize: 1_250_000, progress: 35, status: 'uploading' },
          ],
        },
      },
    },

    // Upload progress: 72%
    {
      id: 'upload-progress-1',
      type: 'inject',
      target: { selector: '[data-demo="upload-queue"]' },
      inject: {
        nodeId: 'upload-page',
        data: {
          demoPhase: 'upload-progress',
          uploadingFiles: [
            { fileName: 'quarterly_revenue.csv', fileSize: 1_250_000, progress: 72, status: 'uploading' },
          ],
        },
      },
      autoAdvanceAfter: 600,
    },

    // Upload progress: 95%
    {
      id: 'upload-progress-2',
      type: 'inject',
      target: { selector: '[data-demo="upload-queue"]' },
      inject: {
        nodeId: 'upload-page',
        data: {
          demoPhase: 'upload-progress',
          uploadingFiles: [
            { fileName: 'quarterly_revenue.csv', fileSize: 1_250_000, progress: 95, status: 'uploading' },
          ],
        },
      },
      autoAdvanceAfter: 400,
    },

    // Processing
    {
      id: 'upload-processing',
      type: 'inject',
      target: { selector: '[data-demo="upload-queue"]' },
      inject: {
        nodeId: 'upload-page',
        data: {
          demoPhase: 'upload-processing',
          uploadingFiles: [
            { fileName: 'quarterly_revenue.csv', fileSize: 1_250_000, progress: 100, status: 'processing', message: 'Parsing CSV\u2026' },
          ],
        },
      },
      autoAdvanceAfter: 1200,
    },

    // Complete
    {
      id: 'upload-complete',
      type: 'inject',
      target: { selector: '[data-demo="upload-queue"]' },
      subtitle: 'Upload complete! The file has been parsed and is ready for analysis.',
      audio: { src: `${AUDIO_BASE}/upload-complete.mp3?${AUDIO_VERSION}`, duration: 4000 },
      inject: {
        nodeId: 'upload-page',
        data: {
          demoPhase: 'upload-complete',
          uploadingFiles: [
            { fileName: 'quarterly_revenue.csv', fileSize: 1_250_000, progress: 100, status: 'completed', message: 'Ready \u2014 available on Sheets' },
          ],
        },
      },
    },

    // Queue highlight
    {
      id: 'queue-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="upload-queue"]' },
      subtitle: 'This is your upload queue. Every file shows up here with real-time progress. You can upload multiple files at once and they\u2019ll all queue up.',
      audio: { src: `${AUDIO_BASE}/queue-highlight.mp3?${AUDIO_VERSION}`, duration: 8000 },
      cursorPath: [
        { x: 0, y: -60, duration: 400 },
        { x: 0, y: 0, duration: 400 },
        { x: 0, y: 40, duration: 300 },
      ],
    },

    // ═══════════════════════════════════════════
    // CLOUD STORAGE TAB
    // ═══════════════════════════════════════════
    {
      id: 'cloud-tab-switch',
      type: 'inject',
      target: { selector: '[data-demo="tab-cloud"]' },
      delayBefore: 300,
      subtitle: 'Now let\u2019s look at cloud storage \u2014 for pulling data directly from your existing cloud buckets.',
      audio: { src: `${AUDIO_BASE}/cloud-tab-intro.mp3?${AUDIO_VERSION}`, duration: 4500 },
      inject: {
        nodeId: 'upload-page',
        data: { demoPhase: 'set-tab', activeTab: 'cloud' },
      },
      cursorPath: [
        { x: -40, y: -20, duration: 400 },
        { x: 0, y: 0, duration: 300 },
      ],
    },
    {
      id: 'cloud-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="cloud-content"]' },
      subtitle: 'Connect AWS S3 buckets or Google Cloud Storage at the organization level, so your whole team can access the same data sources. Browse the file tree, select what you need, and BigSplice transfers it directly \u2014 no download-and-reupload needed. Great for large datasets already sitting in the cloud.',
      audio: { src: `${AUDIO_BASE}/cloud-detail.mp3?${AUDIO_VERSION}`, duration: 19000 },
      cursorPath: [
        { x: 0, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 400 },
        { x: 0, y: 40, duration: 300 },
      ],
    },

    // ═══════════════════════════════════════════
    // SQL INGESTION TAB
    // ═══════════════════════════════════════════
    {
      id: 'sql-tab-switch',
      type: 'inject',
      target: { selector: '[data-demo="tab-sql"]' },
      delayBefore: 300,
      subtitle: 'And here\u2019s SQL ingestion \u2014 connect directly to PostgreSQL, MySQL, or SQL Server databases.',
      audio: { src: `${AUDIO_BASE}/sql-tab-intro.mp3?${AUDIO_VERSION}`, duration: 8000 },
      inject: {
        nodeId: 'upload-page',
        data: { demoPhase: 'set-tab', activeTab: 'sql' },
      },
      cursorPath: [
        { x: -40, y: -20, duration: 400 },
        { x: 0, y: 0, duration: 300 },
      ],
    },
    {
      id: 'sql-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="sql-content"]' },
      subtitle: 'You get a visual table browser with column selection, or write custom SQL queries. Set up scheduled imports \u2014 every fifteen minutes, hourly, daily, or weekly \u2014 and BigSplice pulls the data automatically. Incremental imports are supported, so only new rows get fetched each run. All credentials are encrypted with AWS KMS, and SSH tunnels are built in for databases behind private networks.',
      audio: { src: `${AUDIO_BASE}/sql-detail.mp3?${AUDIO_VERSION}`, duration: 27000 },
      cursorPath: [
        { x: 0, y: -60, duration: 400 },
        { x: 0, y: 0, duration: 500 },
        { x: 0, y: 60, duration: 400 },
      ],
    },

    // ═══════════════════════════════════════════
    // ENDING
    // ═══════════════════════════════════════════
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\u2019s the full ingestion story. Upload files directly, pull from S3 or Google Cloud, or connect to a live database with scheduled imports. Whatever your data source, BigSplice makes it searchable.',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 11000 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Try it myself', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
